import { useEffect, useState } from 'react';
import { toMajor } from '../../contracts/money';
import { useBrand } from '../../brand/BrandProvider';
import { usePackages, usePricesByPackage } from '../../api/catalog';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  CreditCard,
  Database,
  Globe,
  Loader2,
  Server,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';

const STEPS = ['Business details', 'Plan & subdomain', 'Database', 'Payment', 'Confirm'];

/**
 * Add a new business — §5.10
 * Five-step wizard for an existing owner adding another shop.
 */
export function NewBusiness() {
  const navigate = useNavigate();
  const brand = useBrand();
  const [step, setStep] = useState(0);

  // step 1
  const [name, setName] = useState('Khan Bakery — Dhaka');
  const [industry, setIndustry] = useState('Food & beverage');
  const [country, setCountry] = useState('Bangladesh');
  const [currency, setCurrency] = useState('BDT');

  // step 2 — plan catalog from the API (published + public packages; monthly price)
  const { data: packages = [] } = usePackages();
  const { data: prices = [] } = usePricesByPackage();
  const sellable = packages.filter((p) => p.status === 'published' && p.visibility === 'public');
  const monthlyMinor = (pkgId: string) =>
    prices.find((pr) => pr.packageId === pkgId && pr.interval === 'month')?.amount ?? 0;
  const [planId, setPlanId] = useState('');
  useEffect(() => {
    const first = sellable[0];
    if (!planId && first) setPlanId(first.id);
  }, [sellable, planId]);
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');

  // step 3
  const [db, setDb] = useState<'managed' | 'own'>('managed');
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('3306');
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbTls, setDbTls] = useState(true);
  const [dbTest, setDbTest] = useState<'idle' | 'testing' | 'ok'>('idle');

  // step 4
  const [billingChoice, setBillingChoice] = useState<'shared' | 'separate'>('shared');

  const [submitting, setSubmitting] = useState(false);

  function next() {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  }
  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  function testDb() {
    setDbTest('testing');
    setTimeout(() => setDbTest('ok'), 1100);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      next();
      return;
    }
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/businesses' }), 1100);
  }

  const selectedPkg = sellable.find((p) => p.id === planId);
  const selectedMinor = monthlyMinor(planId);
  const selLoc = selectedPkg?.limits.locations;
  const allowsCustomDomain = selLoc === 'unlimited' || (typeof selLoc === 'number' && selLoc >= 3);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Add a new business"
        subtitle="Each business gets its own subscription, POS, and (on Pro+) its own database."
      />

      <div className="mb-8 flex justify-center">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="p-5 sm:p-8">
        <form onSubmit={onSubmit} className="space-y-6">
          {step === 0 && (
            <div className="space-y-5">
              <Input
                label="Business name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                prefix={<Building2 className="w-4 h-4" />}
              />
              <div className="grid sm:grid-cols-2 gap-5">
                <SelectField
                  label="Industry"
                  value={industry}
                  onChange={setIndustry}
                  options={['Food & beverage', 'Retail', 'Pharmacy', 'Service', 'Manufacturing']}
                />
                <SelectField
                  label="Country"
                  value={country}
                  onChange={setCountry}
                  options={['Bangladesh', 'India', 'Pakistan', 'Sri Lanka', 'Other']}
                />
              </div>
              <SelectField
                label="Currency"
                value={currency}
                onChange={setCurrency}
                options={['BDT', 'USD', 'INR', 'PKR', 'LKR']}
              />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  Plan
                </label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sellable.map((p) => (
                    <OptionCard
                      key={p.id}
                      selected={planId === p.id}
                      onSelect={() => setPlanId(p.id)}
                      className="text-center"
                    >
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">{p.name}</div>
                      <Money
                        minor={monthlyMinor(p.id)}
                        hideCents
                        tone="neutral"
                        className="mt-1 block font-semibold text-zinc-950 dark:text-zinc-50 text-lg"
                      />
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">/ month</div>
                    </OptionCard>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                  Subdomain
                </label>
                <div className="flex items-stretch rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 focus-within:ring-2 focus-within:ring-brand dark:focus-within:ring-brand-dm">
                  <input
                    type="text"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                    className="flex-1 px-3 py-2.5 text-sm bg-transparent text-zinc-950 dark:text-zinc-50 focus:outline-none mono"
                  />
                  <span className="px-3 py-2.5 text-sm bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-l border-zinc-200 dark:border-zinc-700 mono">
                    .{brand.domain}
                  </span>
                </div>
                <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Available
                </p>
              </div>

              {allowsCustomDomain && (
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    Custom domain <span className="text-xs font-normal text-zinc-500">(optional)</span>
                  </label>
                  <Input
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    placeholder="pos.your-shop.com"
                    prefix={<Globe className="w-4 h-4" />}
                  />
                  {customDomain && (
                    <div className="mt-2 px-3 py-2 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-600 dark:text-zinc-300">
                      Add a CNAME{' '}
                      <code className="mono">{customDomain}</code> →{' '}
                      <code className="mono">cname.{brand.domain}</code>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  Database (Pro+)
                </label>
                <div className="space-y-3">
                  <OptionCard
                    selected={db === 'managed'}
                    onSelect={() => setDb('managed')}
                  >
                    <div className="flex items-start gap-3">
                      <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                      <div>
                        <div className="font-medium text-zinc-950 dark:text-zinc-50">
                          Managed (recommended)
                        </div>
                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                          We provision and operate the database for you.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                  <OptionCard selected={db === 'own'} onSelect={() => setDb('own')}>
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 mt-0.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-zinc-950 dark:text-zinc-50">
                          My own MySQL
                        </div>
                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                          Bring your own server. We connect over TLS.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                </div>
              </div>

              {db === 'own' && (
                <div className="space-y-4 rounded-lg border border-zinc-200 dark:border-zinc-600 p-4">
                  <div className="grid sm:grid-cols-[1fr_auto] gap-3">
                    <Input
                      label="Host"
                      required
                      value={dbHost}
                      onChange={(e) => setDbHost(e.target.value)}
                      placeholder="pos-db.your-server.com"
                    />
                    <Input
                      label="Port"
                      required
                      value={dbPort}
                      onChange={(e) => setDbPort(e.target.value)}
                      className="sm:w-24"
                    />
                  </div>
                  <Input
                    label="Database name"
                    required
                    value={dbName}
                    onChange={(e) => setDbName(e.target.value)}
                  />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      label="Admin user"
                      required
                      value={dbUser}
                      onChange={(e) => setDbUser(e.target.value)}
                    />
                    <Input
                      label="Password"
                      type="password"
                      required
                      value={dbPassword}
                      onChange={(e) => setDbPassword(e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
                    <input
                      type="checkbox"
                      checked={dbTls}
                      onChange={(e) => setDbTls(e.target.checked)}
                      className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                    />
                    Server requires TLS
                  </label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={testDb}
                      iconLeft={
                        dbTest === 'testing' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : undefined
                      }
                    >
                      {dbTest === 'testing' ? 'Testing…' : 'Test connection'}
                    </Button>
                    {dbTest === 'ok' && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
                        <CheckCircle2 className="w-4 h-4" />
                        Connected · MySQL 8.0.30 · 12 ms
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-700 dark:text-zinc-200">
                Same payment method as your existing subscription
              </div>
              <OptionCard
                selected={billingChoice === 'shared'}
                onSelect={() => setBillingChoice('shared')}
              >
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                  <div>
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">
                      Use SSLCommerz auto-charge
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                      Next charge: 09 May 2026 · Card ending 4242
                    </p>
                  </div>
                </div>
              </OptionCard>
              <OptionCard
                selected={billingChoice === 'separate'}
                onSelect={() => setBillingChoice('separate')}
              >
                <div className="font-medium text-zinc-950 dark:text-zinc-50">
                  Pay separately for this business
                </div>
                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                  Lets your accountant track billings per business.
                </p>
              </OptionCard>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 p-5">
                <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-3">
                  Confirm
                </div>
                <dl className="text-sm space-y-2.5">
                  <SummaryRow label="Business" value={name} />
                  <SummaryRow label="Industry" value={industry} />
                  <SummaryRow label="Plan" value={`${selectedPkg?.name ?? '—'} · ৳${toMajor(selectedMinor)}/mo`} />
                  <SummaryRow
                    label="URL"
                    value={
                      <span className="mono text-xs">
                        {subdomain}.{brand.domain}
                      </span>
                    }
                  />
                  <SummaryRow
                    label="Database"
                    value={db === 'managed' ? 'Managed' : 'Self-hosted'}
                  />
                  <SummaryRow
                    label="Billing"
                    value={
                      billingChoice === 'shared'
                        ? 'Same as existing subscription'
                        : 'Separate billing'
                    }
                  />
                </dl>
              </div>

              <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm">
                <div className="font-medium text-emerald-900 dark:text-emerald-200 mb-1">
                  Total today: ৳0 (trial)
                </div>
                <div className="text-emerald-800 dark:text-emerald-300 text-xs">
                  First charge on 09 May · ৳{toMajor(selectedMinor)}
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 mt-2 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button
                type="button"
                variant="ghost"
                onClick={back}
                iconLeft={<ArrowLeft className="w-4 h-4" />}
              >
                Back
              </Button>
            ) : (
              <Button type="button" variant="ghost" onClick={() => navigate({ to: '/businesses' })}>
                Cancel
              </Button>
            )}

            <Button
              type="submit"
              loading={submitting}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {step < STEPS.length - 1 ? 'Continue' : 'Create business'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'block w-full px-3 py-2.5 text-sm rounded-md',
          'bg-white text-zinc-950 dark:bg-zinc-900 dark:text-zinc-50',
          'border border-zinc-300 dark:border-zinc-700',
          'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
        )}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wide w-32 flex-shrink-0">
        {label}
      </dt>
      <dd className="text-right text-zinc-900 dark:text-zinc-100 font-medium">{value}</dd>
    </div>
  );
}
